import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { format } from 'date-fns';
import { useData } from '../contexts/DataContext';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import SectionHeader from '../components/SectionHeader';

const { FiPlus, FiTool, FiTrash2, FiAlertCircle } = FiIcons;

const MaintenanceLog = () => {
  const { data, addMaintenanceLog, deleteEntry } = useData();
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState({
    type: 'filter',
    description: '',
    notes: '',
    nextMaintenanceDate: ''
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    addMaintenanceLog(formData);
    setFormData({
      type: 'filter',
      description: '',
      notes: '',
      nextMaintenanceDate: ''
    });
    setShowForm(false);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  return (
    <div className="p-4 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Maintenance Log</h1>
          <p className="text-gray-600 mt-1">Track system maintenance and repairs</p>
        </div>
        <button
          onClick={() => setShowForm(true)}
          className="flex items-center space-x-2 bg-orange-500 text-white px-4 py-2 rounded-lg hover:bg-orange-600 transition-colors"
        >
          <SafeIcon icon={FiPlus} className="w-4 h-4" />
          <span>Add Log</span>
        </button>
      </div>

      {/* Maintenance Best Practices */}
      <div className="bg-orange-50 p-4 rounded-lg">
        <h3 className="text-lg font-semibold text-orange-800 mb-2">Maintenance Best Practices</h3>
        <div className="space-y-2 text-orange-700">
          <p className="flex items-center">
            <span className="w-4 h-4 inline-flex items-center justify-center bg-orange-200 rounded-full mr-2">1</span>
            Clean filters weekly to maintain water quality
          </p>
          <p className="flex items-center">
            <span className="w-4 h-4 inline-flex items-center justify-center bg-orange-200 rounded-full mr-2">2</span>
            Check aeration system daily
          </p>
          <p className="flex items-center">
            <span className="w-4 h-4 inline-flex items-center justify-center bg-orange-200 rounded-full mr-2">3</span>
            Monitor pump performance and clean monthly
          </p>
          <p className="flex items-center">
            <span className="w-4 h-4 inline-flex items-center justify-center bg-orange-200 rounded-full mr-2">4</span>
            Inspect tanks for damage weekly
          </p>
        </div>
      </div>

      {/* Maintenance Schedule Guide */}
      <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-200">
        <SectionHeader 
          title="Maintenance Schedule Guide" 
          infoText="Regular maintenance helps prevent system failures and ensures optimal conditions for fish growth."
        />
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="p-3 bg-gray-50 rounded-lg">
            <h4 className="font-medium text-gray-900 mb-2">Daily Tasks</h4>
            <ul className="space-y-1 text-sm text-gray-600">
              <li>• Check water flow and aeration</li>
              <li>• Inspect for leaks</li>
              <li>• Monitor equipment operation</li>
              <li>• Remove any debris</li>
            </ul>
          </div>
          <div className="p-3 bg-gray-50 rounded-lg">
            <h4 className="font-medium text-gray-900 mb-2">Weekly Tasks</h4>
            <ul className="space-y-1 text-sm text-gray-600">
              <li>• Clean filters</li>
              <li>• Check pump performance</li>
              <li>• Inspect tank walls</li>
              <li>• Test backup systems</li>
            </ul>
          </div>
          <div className="p-3 bg-gray-50 rounded-lg">
            <h4 className="font-medium text-gray-900 mb-2">Monthly Tasks</h4>
            <ul className="space-y-1 text-sm text-gray-600">
              <li>• Deep clean filters</li>
              <li>• Calibrate equipment</li>
              <li>• Check electrical connections</li>
              <li>• Service pumps</li>
            </ul>
          </div>
          <div className="p-3 bg-gray-50 rounded-lg">
            <h4 className="font-medium text-gray-900 mb-2">Quarterly Tasks</h4>
            <ul className="space-y-1 text-sm text-gray-600">
              <li>• Major system inspection</li>
              <li>• Replace worn components</li>
              <li>• Update maintenance records</li>
              <li>• Review emergency procedures</li>
            </ul>
          </div>
        </div>
      </div>

      {/* Add Maintenance Log Form */}
      {showForm && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white rounded-xl p-6 shadow-sm border border-gray-200"
        >
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Record Maintenance</h2>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Maintenance Type
              </label>
              <select
                name="type"
                value={formData.type}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
              >
                <option value="filter">Filter Maintenance</option>
                <option value="pump">Pump Service</option>
                <option value="aeration">Aeration System</option>
                <option value="tank">Tank Maintenance</option>
                <option value="plumbing">Plumbing</option>
                <option value="electrical">Electrical</option>
                <option value="other">Other</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Description
              </label>
              <input
                type="text"
                name="description"
                value={formData.description}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Next Maintenance Due
              </label>
              <input
                type="date"
                name="nextMaintenanceDate"
                value={formData.nextMaintenanceDate}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Notes
              </label>
              <textarea
                name="notes"
                value={formData.notes}
                onChange={handleInputChange}
                rows={3}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                placeholder="Additional details, parts replaced, or observations..."
              />
            </div>
            <div className="flex space-x-3">
              <button
                type="submit"
                className="flex-1 bg-orange-500 text-white py-2 px-4 rounded-lg hover:bg-orange-600 transition-colors"
              >
                Save Log
              </button>
              <button
                type="button"
                onClick={() => setShowForm(false)}
                className="flex-1 bg-gray-500 text-white py-2 px-4 rounded-lg hover:bg-gray-600 transition-colors"
              >
                Cancel
              </button>
            </div>
          </form>
        </motion.div>
      )}

      {/* Maintenance History */}
      <div className="space-y-4">
        <SectionHeader 
          title="Maintenance History" 
          infoText="Record of all maintenance activities. Use this to track patterns and plan future maintenance."
        />
        {data.maintenanceLogs.length > 0 ? (
          [...data.maintenanceLogs].reverse().map((log) => (
            <motion.div
              key={log.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-white rounded-xl p-4 shadow-sm border border-gray-200"
            >
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center space-x-3">
                  <div className="w-10 h-10 bg-orange-100 rounded-lg flex items-center justify-center">
                    <SafeIcon icon={FiTool} className="w-5 h-5 text-orange-600" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900">
                      {log.description}
                    </h3>
                    <p className="text-sm text-gray-500">
                      {format(new Date(log.date), 'MMM dd, yyyy')}
                    </p>
                  </div>
                </div>
                <button
                  onClick={() => deleteEntry('maintenanceLogs', log.id)}
                  className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                >
                  <SafeIcon icon={FiTrash2} className="w-4 h-4" />
                </button>
              </div>
              <div className="space-y-3">
                <div className="flex justify-between items-center">
                  <span className="text-sm text-gray-600">Type</span>
                  <span className="text-sm font-medium capitalize">{log.type}</span>
                </div>
                {log.nextMaintenanceDate && (
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-gray-600">Next Due</span>
                    <span className="text-sm font-medium">
                      {format(new Date(log.nextMaintenanceDate), 'MMM dd, yyyy')}
                    </span>
                  </div>
                )}
                {log.notes && (
                  <div className="mt-4 p-3 bg-gray-50 rounded-lg">
                    <p className="text-sm text-gray-700">{log.notes}</p>
                  </div>
                )}
              </div>
            </motion.div>
          ))
        ) : (
          <div className="text-center py-12">
            <SafeIcon icon={FiTool} className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">No maintenance logs yet</h3>
            <p className="text-gray-500 mb-6">Start tracking your maintenance activities by adding your first log</p>
            <button
              onClick={() => setShowForm(true)}
              className="inline-flex items-center space-x-2 bg-orange-500 text-white px-6 py-3 rounded-lg hover:bg-orange-600 transition-colors"
            >
              <SafeIcon icon={FiPlus} className="w-5 h-5" />
              <span>Add First Log</span>
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default MaintenanceLog;